import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { LogoBase64 } from "~/assets/images/logobase64";
import type { WorkflowSettings } from "~/models/types";

/**
 * Combines multiple class names into a single string.
 *
 * @param inputs - The class names to be combined.
 * @returns The combined class names as a string.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export function handleEnterKeyFocusNext(event) {
  if (event.key === "Enter") {
    event.preventDefault();
    const form = event.target.form;
    const index = Array.from(form.elements).indexOf(event.target);
    const nextElement = form.elements[index + 1];
    nextElement?.focus();
  }
}

/**
 * Checks if a value is truthy according to specific criteria.
 * @param value The value to check
 * @returns true if the value is "1", 1, or "true", false otherwise
 */
export function isTruthy(
  value: string | number | boolean | null | undefined
): boolean {
  return value === "1" || value === 1 || value === "true" || value === true;
}

/**
 * Checks whether an examination language is German, including regional
 * variants such as `de-CH`. A missing language counts as German, matching the
 * app's German fallback.
 */
export function isGermanLanguage(isoCode: string | null | undefined): boolean {
  return !isoCode || isoCode.toLowerCase().split(/[-_]/)[0] === "de";
}

/**
 * Decides whether an examination's slit lamp sections are shown and have to be
 * classified.
 *
 * Event-based examinations keep their own flag. Location-based examinations —
 * the ones mira-web produces — have no event, so they are covered by the slit
 * lamp workflow setting that was copied onto the examination at check-in.
 *
 * This deliberately reads the examination's own `workflowSettings` instead of
 * going through `resolveWorkflowSettings()`: that resolver falls back to the
 * tenant, which would let a later tenant change add a slit lamp section to an
 * examination that was captured and reviewed without one.
 */
export const isSlitLampEnabled = (examination: {
  event?: {
    settings?: {
      modality?: { enableSlitLamp?: string | number | boolean | null };
    };
  };
  workflowSettings?: WorkflowSettings;
}): boolean =>
  isTruthy(examination.event?.settings?.modality?.enableSlitLamp) ||
  examination.workflowSettings?.slitLamp?.enabled === true;

export const resolveWorkflowSettings = (examination) => {
  /*
   * if examination workflow settings exist, use that
   * otherwise fallback to event workflow settings if it exists
   * otherwise fallback to tenant workflow settings
   */
  return examination.workflowSettings
    ? examination.workflowSettings
    : examination.event?.workflowSettings
    ? examination.event.workflowSettings
    : examination.tenant?.workflowSettings;
};

export const getTenantConfig = (examination: {
  tenant: {
    id: string;
    name: string;
    displayName: string;
    type: string;
    settings?: {
      logo: string;
    };
    reportSettings?: {
      title: string;
      bgColor: string;
      textColor: string;
      serviceNumber: string;
    };
  };
  isInternal: boolean;
}) => {
  const defaultConfig = {
    logo: examination.tenant.settings?.logo || LogoBase64,
    title: examination.tenant.reportSettings?.title,
    name: examination.tenant.name,
    displayName: examination.tenant.displayName,
    tenantType: examination.tenant.type || "SERVICE_PROVIDER",
    bgColor: examination.tenant.reportSettings?.bgColor,
    textColor: examination.tenant.reportSettings?.textColor,
    serviceNumber: examination.tenant.reportSettings?.serviceNumber,
  };

  return examination.isInternal
    ? {
        ...defaultConfig,
        logo: examination.tenant.settings?.logo,
        title: examination.tenant?.reportSettings?.title,
        name: examination.tenant.name,
        displayName: examination.tenant.displayName,
        tenantType: examination.tenant.type,
        bgColor: examination.tenant?.reportSettings?.bgColor,
        textColor: examination.tenant?.reportSettings?.textColor,
        serviceNumber: examination.tenant?.reportSettings?.serviceNumber,
      }
    : defaultConfig;
};
